import React, { useEffect, useMemo, useRef, useState } from "react";
import "./resultados.css";

function Resultados() {
    const [open, setOpen] = useState(null); // 'comp' | 'jor' | 'team' | null
    const toggleOpen = (name) => setOpen((prev) => (prev === name ? null : name));
    const closeAll = () => setOpen(null);

    const [competiciones, setCompeticiones] = useState([]);
    const [seleccion, setSeleccion] = useState(null);
    const [datos, setDatos] = useState([]);
    const [codeMap, setCodeMap] = useState(new Map());

    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState("");

    const [jornadaSel, setJornadaSel] = useState(null);
    const [teamSel, setTeamSel] = useState(null);

    const [showEdit, setShowEdit] = useState(false);
    const [editDoc, setEditDoc] = useState(null);
    const [confirmDel, setConfirmDel] = useState(null);

    const [fCATEGORIA, setFCATEGORIA] = useState("");
    const [fJORNADA, setFJORNADA] = useState("");
    const [fFECHA, setFFECHA] = useState("");
    const [fHORA, setFHORA] = useState("");
    const [fUBICACION, setFUBICACION] = useState("");
    const [fLOCAL, setFLOCAL] = useState("");
    const [fVISIT, setFVISIT] = useState("");
    const [fGOLES_L, setFGOLES_L] = useState("");
    const [fGOLES_V, setFGOLES_V] = useState("");

    const [origLocal, setOrigLocal] = useState("");
    const [origVisit, setOrigVisit] = useState("");
    const [origJornada, setOrigJornada] = useState(null);
    const [origFechaYYYYMMDD, setOrigFechaYYYYMMDD] = useState("");

    const dropdownRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                closeAll();
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") closeAll();
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    const displayNombre = (raw) => {
        if (!raw) return "";
        const pretty = raw.replace(/_/g, " ").toLowerCase();
        return pretty.charAt(0).toUpperCase() + pretty.slice(1);
    };
    const safe = (obj, key) => (obj?.[key] ?? obj?.[key?.toLowerCase?.()] ?? "");
    const formatFecha = (v) => (typeof v === "string" ? v : "");

    useEffect(() => {
        fetch("/api/resultados/competiciones")
            .then((r) => {
                if (!r.ok) throw new Error("No se pudieron cargar las competiciones");
                return r.json();
            })
            .then(setCompeticiones)
            .catch((e) => setError(e.message));

        fetch("/api/clubs/codes-map")
            .then((r) => (r.ok ? r.json() : {}))
            .then((obj) => {
                const m = new Map();
                for (const [k, v] of Object.entries(obj || {})) {
                    if (k && v) m.set(String(k).trim(), String(v));
                }
                setCodeMap(m);
            })
            .catch(() => setCodeMap(new Map()));
    }, []);

    const cargarCompeticion = async (name) => {
        setSeleccion(name);
        closeAll();
        setJornadaSel(null);
        setTeamSel(null);
        setCargando(true);
        setError("");
        try {
            const r = await fetch(`/api/resultados?coleccion=${encodeURIComponent(name)}`);
            if (!r.ok) throw new Error(`No se pudieron cargar los resultados de ${name}`);
            const data = await r.json();
            setDatos(data || []);
        } catch (e) {
            setError(e.message);
            setDatos([]);
        } finally {
            setCargando(false);
        }
    };

    const jornadas = useMemo(() => {
        const map = new Map();
        for (const it of datos) {
            const j = it["JORNADA"] ?? it["jornada"] ?? 0;
            if (!map.has(j)) map.set(j, []);
            map.get(j).push(it);
        }
        return Array.from(map.entries())
            .sort((a, b) => (a[0] ?? 0) - (b[0] ?? 0))
            .map(([jornada, items]) => ({
                jornada,
                items: items.sort((a, b) => {
                    const da = a["FECHA"] ? new Date(a["FECHA"]) : null;
                    const db = b["FECHA"] ? new Date(b["FECHA"]) : null;
                    return (da?.getTime() || 0) - (db?.getTime() || 0);
                }),
            }));
    }, [datos]);

    const jornadasDisponibles = useMemo(() => jornadas.map((j) => j.jornada), [jornadas]);

    const teamsDisponibles = useMemo(() => {
        const s = new Set();
        for (const it of datos) {
            const l = safe(it, "EQUIPO_LOCAL");
            const v = safe(it, "EQUIPO_VISITANTE");
            if (l && typeof l === "string") s.add(l);
            if (v && typeof v === "string") s.add(v);
        }
        return Array.from(s).sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));
    }, [datos]);

    const jornadasFiltradas = useMemo(() => {
        let base = jornadas;
        if (jornadaSel != null) base = base.filter((j) => j.jornada === jornadaSel);
        if (teamSel) {
            base = base
                .map(({ jornada, items }) => ({
                    jornada,
                    items: items.filter(
                        (m) =>
                            safe(m, "EQUIPO_LOCAL") === teamSel ||
                            safe(m, "EQUIPO_VISITANTE") === teamSel
                    ),
                }))
                .filter((j) => j.items.length > 0);
        }
        return base;
    }, [jornadas, jornadaSel, teamSel]);

    const resolveClubId = (externalIdString) => {
        const key = (externalIdString ?? "").toString().trim();
        if (!key) return null;
        const byCode = codeMap.get(key);
        if (byCode) return byCode;
        if (/^[0-9a-fA-F]{24}$/.test(key)) return key;
        return null;
    };

    return (
        <div className="res-wrap" ref={dropdownRef}>
            {/* Header */}
            <div className="res-header">
                <h1 className="title">Resultados</h1>

                <div className="res-right">
                    {/* Competición */}
                    <div className="dropdown">
                        <button className="btn-create" onClick={() => toggleOpen("comp")}>
                            {seleccion
                                ? `Competición: ${displayNombre(seleccion)}`
                                : "Seleccionar competición"}
                        </button>
                        {open === "comp" && (
                            <div className="dropdown-panel" role="menu">
                                {competiciones.length === 0 && (
                                    <div className="dropdown-empty">Cargando competiciones</div>
                                )}
                                {[...competiciones]
                                    .sort((a, b) =>
                                        a.localeCompare(b, "es", { sensitivity: "base" })
                                    )
                                    .map((name) => (
                                        <button
                                            key={name}
                                            className="dropdown-item"
                                            onClick={() => {
                                                cargarCompeticion(name);
                                                closeAll();
                                            }}
                                        >
                                            {displayNombre(name)}
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>

                    {/* Jornada */}
                    {seleccion && (
                        <div className="dropdown">
                            <button className="btn-create" onClick={() => toggleOpen("jor")}>
                                {jornadaSel == null
                                    ? "Filtrar por jornada"
                                    : `Jornada: ${jornadaSel}`}
                            </button>
                            {open === "jor" && (
                                <div className="dropdown-panel" role="menu">
                                    <button
                                        className="dropdown-item"
                                        onClick={() => {
                                            setJornadaSel(null);
                                            closeAll();
                                        }}
                                    >
                                        Todas las jornadas
                                    </button>
                                    {jornadasDisponibles.map((j) => (
                                        <button
                                            key={j}
                                            className="dropdown-item"
                                            onClick={() => {
                                                setJornadaSel(j);
                                                closeAll();
                                            }}
                                        >
                                            Jornada {j}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Equipo */}
                    {seleccion && (
                        <div className="dropdown">
                            <button className="btn-create" onClick={() => toggleOpen("team")}>
                                {teamSel ? `Equipo: ${teamSel}` : "Filtrar por equipo"}
                            </button>
                            {open === "team" && (
                                <div className="dropdown-panel" role="menu">
                                    <button
                                        className="dropdown-item"
                                        onClick={() => {
                                            setTeamSel(null);
                                            closeAll();
                                        }}
                                    >
                                        Todos los equipos
                                    </button>
                                    {teamsDisponibles.map((t) => (
                                        <button
                                            key={t}
                                            className="dropdown-item"
                                            onClick={() => {
                                                setTeamSel(t);
                                                closeAll();
                                            }}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Estados */}
            {error && <div className="res-error">{error}</div>}
            {cargando && <div className="res-loading">Cargando…</div>}

            {/* Jornadas */}
            {!cargando && !error && seleccion && (
                <div className="res-list">
                    {jornadasFiltradas.map(({ jornada, items }) => (
                        <section className="jornada-card" key={jornada}>
                            <header className="jornada-header">
                                <h2>Jornada {jornada}</h2>
                            </header>

                            <div className="partidos">
                                {items.map((m) => {
                                    const local = safe(m, "EQUIPO_LOCAL");
                                    const visit = safe(m, "EQUIPO_VISITANTE");
                                    const golesL = m["GOLES_LOCAL"];
                                    const golesV = m["GOLES_VISITANTE"];
                                    const fecha = formatFecha(m["FECHA"]);
                                    const hora = m["HORA"] || "";
                                    const ubic = m["UBICACIÓN"] || m["UBICACION"] || "";

                                    const normalizeId = (v) => {
                                        if (!v) return "";
                                        const str = v.toString().trim();
                                        if (/^\d+\.0$/.test(str)) return String(parseInt(str, 10));
                                        return str;
                                    };

                                    const idLocalExt = normalizeId(
                                        m.id_local ?? m.ID_LOCAL ?? m.Id_Local ?? ""
                                    );
                                    const idVisitExt = normalizeId(
                                        m.id_visitante ?? m.ID_VISITANTE ?? m.Id_Visitante ?? ""
                                    );

                                    const localClubId = resolveClubId(idLocalExt);
                                    const visitClubId = resolveClubId(idVisitExt);

                                    return (
                                        <div
                                            className="partido-row clickable"
                                            key={m._id || `${local}-${visit}-${jornada}-${fecha}`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setShowEdit(true)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    setShowEdit(true);
                                                }
                                            }}
                                        >
                                            <div className="partido-left">
                                                <div className="fecha">{fecha}</div>
                                                {hora ? <div className="hora">{hora}</div> : null}
                                            </div>

                                            <div className="partido-center">
                                                <div className="equipos">
                                                    <span className="escudo-wrap">
                                                        {localClubId ? (
                                                            <img
                                                                className="escudo-mini"
                                                                src={`/api/clubs/${localClubId}/escudo`}
                                                                alt={`Escudo ${local}`}
                                                                onError={(e) =>
                                                                    (e.currentTarget.style.display =
                                                                        "none")
                                                                }
                                                            />
                                                        ) : null}
                                                        <span className="equipo local">{local}</span>
                                                    </span>

                                                    <span className="vs">vs</span>

                                                    <span className="escudo-wrap">
                                                        <span className="equipo visitante">
                                                            {visit}
                                                        </span>
                                                        {visitClubId ? (
                                                            <img
                                                                className="escudo-mini"
                                                                src={`/api/clubs/${visitClubId}/escudo`}
                                                                alt={`Escudo ${visit}`}
                                                                onError={(e) =>
                                                                    (e.currentTarget.style.display =
                                                                        "none")
                                                                }
                                                            />
                                                        ) : null}
                                                    </span>
                                                </div>
                                                {ubic && <div className="ubic">{ubic}</div>}
                                            </div>

                                            <div className="partido-right">
                                                {golesL != null &&
                                                golesV != null &&
                                                `${golesL}`.trim() !== "" &&
                                                `${golesV}`.trim() !== "" ? (
                                                    <div className="marcador">
                                                        <span className="g">{golesL}</span>
                                                        <span className="sep">-</span>
                                                        <span className="g">{golesV}</span>
                                                    </div>
                                                ) : (
                                                    <div className="pendiente">Pendiente</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    ))}

                    {jornadasFiltradas.length === 0 && (
                        <div className="res-empty">
                            No hay datos
                            {jornadaSel != null ? ` para la jornada ${jornadaSel}` : ""}
                            {teamSel ? ` del equipo ${teamSel}` : ""} en{" "}
                            <strong>{displayNombre(seleccion)}</strong>.
                        </div>
                    )}
                </div>
            )}

            {!seleccion && !cargando && (
                <div className="res-empty">
                    Selecciona una competición para ver sus jornadas.
                </div>
            )}
        </div>
    );
}

export default Resultados;
